export const GUIDE_VERSION = 1;
export const guideSteps = [
  {
    label: "Bienvenida",
    title: "Tu negocio, conectado con Delibrán",
    description:
      "Te acompañamos en los primeros pasos. Conoce cada herramienta y prepara tu negocio a tu ritmo.",
    icon: "welcome",
    eyebrow: "Bienvenido a tu espacio de trabajo",
    cards: [
      {
        title: "Admin",
        text: "El centro de control: configura tu negocio, organiza la carta y consulta pedidos y clientes.",
      },
      {
        title: "TPV y tienda online",
        text: "Los puntos donde vendes: atiende en el local con el TPV y ofrece tu carta en la tienda online.",
      },
      {
        title: "Gestor de pedidos y repartidor",
        text: "Las herramientas de tu equipo para preparar los pedidos y gestionar su entrega, según los accesos configurados.",
      },
    ],
    tip: "Esta guía explica la plataforma. No publica productos, activa canales ni cambia los ajustes de tu negocio.",
  },
  {
    label: "Tu negocio",
    title: "Empieza por los datos de tu negocio",
    description:
      "En Configuración encontrarás los ajustes que dan contexto a la tienda, al TPV y a tu equipo.",
    icon: "settings",
    eyebrow: "1 · Los datos básicos",
    cards: [
      {
        title: "Información general",
        text: "Revisa el nombre, los datos fiscales, la dirección y los contactos del negocio y del propietario.",
      },
      {
        title: "Horario",
        text: "Define los días y las horas de apertura. Revisa también los festivos y las fechas especiales.",
      },
      {
        title: "Pagos y entrega",
        text: "Comprueba los métodos de pago y las opciones de recogida o envío que vas a ofrecer.",
      },
    ],
    tip: "Si vas a usar facturación o pagos online, revisa su configuración antes de comenzar a operar.",
    path: "/configuration#100",
    action: "Ir a información general",
  },
  {
    label: "Diseño",
    title: "Dale a la tienda la identidad de tu marca",
    description:
      "La sección Diseño reúne los elementos visuales que reconocerán tus clientes.",
    icon: "design",
    eyebrow: "2 · La imagen de tu negocio",
    cards: [
      {
        title: "Tus logos",
        text: "Añade el logo principal y el logo para los tickets. El favicon está en Más opciones.",
      },
      {
        title: "Color y apariencia",
        text: "Elige el color primario y la distribución de la carta: Lista o Galería.",
      },
      {
        title: "Revisa el resultado",
        text: "Guarda los cambios y comprueba cómo se ve la tienda antes de compartirla con tus clientes.",
      },
    ],
    tip: "Los cambios que hagas en los formularios se aplican al guardarlos; revisa los mensajes de confirmación.",
    path: "/configuration#108",
    action: "Ir a Diseño",
  },
  {
    label: "Tu carta",
    title: "Organiza los productos que vas a vender",
    description:
      "Productos, categorías y catálogos trabajan juntos para construir tu carta.",
    icon: "products",
    eyebrow: "3 · Prepara tu catálogo",
    cards: [
      {
        title: "Productos",
        text: "Revisa nombre, imagen, precio con IVA, disponibilidad y alérgenos. Añade variantes o extras cuando corresponda.",
      },
      {
        title: "Categorías y catálogos",
        text: "Agrupa los productos en categorías y organiza las colecciones que publicarás en tus canales de venta.",
      },
      {
        title: "Borradores y publicación",
        text: "Puedes preparar productos en borrador. Antes de publicarlos, revisa la información pendiente y sus asociaciones.",
      },
    ],
    tip: "Comprueba los precios y la información del producto en cada canal antes de empezar a vender.",
    path: "/products",
    action: "Ir a productos",
  },
  {
    label: "Canales de venta",
    title: "Decide dónde y cómo recibes pedidos",
    description:
      "Un canal de venta representa una forma de vender, como el mostrador o la tienda online.",
    icon: "channels",
    eyebrow: "4 · Conecta tu operativa",
    cards: [
      {
        title: "Canales de venta",
        text: "Abre cada canal y revisa su estado, los productos disponibles y los métodos admitidos de pago y entrega.",
      },
      {
        title: "Terminales y usuarios",
        text: "En la ficha de cada canal TPV, abre Terminales para configurar los puestos, sus usuarios y los canales que pueden visualizar.",
      },
      {
        title: "Gestor de pedidos",
        text: "Configura los perfiles de preparación y comprueba los accesos y las impresoras que necesita el equipo.",
      },
    ],
    tip: "Las plataformas necesitan sus accesos configurados. Conocer esta guía no activa esos servicios automáticamente.",
    path: "/salesChannel",
    action: "Ir a canales de venta",
  },
  {
    label: "Tu equipo",
    title: "Cada persona, con su acceso y su función",
    description:
      "Usuarios y permisos te ayuda a organizar quién trabaja en el negocio y qué herramientas utiliza.",
    icon: "team",
    eyebrow: "5 · Prepara los accesos",
    cards: [
      {
        title: "Usuarios y roles",
        text: "Añade a las personas del equipo y revisa su rol y los permisos de las plataformas que necesitan.",
      },
      {
        title: "Acceso al Admin",
        text: "Los administradores acceden al panel con sus propias credenciales. Revisa la invitación cuando corresponda.",
      },
      {
        title: "PIN personal",
        text: "El acceso general abre una plataforma operativa; después, cada trabajador se identifica con su PIN cuando el flujo lo requiere.",
      },
    ],
    tip: "Evita compartir contraseñas y PIN. Revisa los permisos cuando cambien las responsabilidades de una persona.",
    path: "/configuration#101",
    action: "Ir a usuarios y permisos",
  },
  {
    label: "El día a día",
    title: "Ya sabes cómo orientarte en el Admin",
    description:
      "Estas son las secciones que te acompañarán cuando el negocio esté trabajando.",
    icon: "orders",
    eyebrow: "6 · Tu siguiente paso",
    cards: [
      {
        title: "Inicio y pedidos",
        text: "Consulta la información disponible del negocio. Busca y filtra pedidos, y abre su ficha para revisar productos, importes, pago y entrega.",
      },
      {
        title: "Clientes y descuentos",
        text: "Consulta las fichas y el historial de tus clientes. Gestiona los códigos promocionales y sus condiciones desde Descuentos.",
      },
      {
        title: "Ayuda, cuando la necesites",
        text: "Desde Ayuda puedes repasar esta guía y consultar las instrucciones de cada sección.",
      },
    ],
    tip: "Completar la guía no significa que el negocio esté configurado. Revisa datos, carta, canales y accesos antes de operar.",
    path: "/configuration#100",
    action: "Empezar por la configuración",
  },
];
export const parseGuideResponse = (response) => {
  const value = response?.data;
  if (
    response?.error !== false ||
    !value ||
    typeof value.eligible !== "boolean"
  )
    throw Error("No se pudo cargar la guía de inicio.");
  if (!value.eligible) return { eligible: false };
  if (
    value.version !== GUIDE_VERSION ||
    !["new", "in_progress", "paused", "completed"].includes(value.status) ||
    !Number.isInteger(value.step) ||
    value.step < 0 ||
    value.step >= guideSteps.length ||
    (value.status === "completed" &&
      (value.step !== guideSteps.length - 1 ||
        !Number.isFinite(Date.parse(value.completedAt))))
  )
    throw Error("No se pudo confirmar el progreso de la guía.");
  return value;
};
